/**
 * Assessment list.
 *
 * Also the place that explains *why* a new submission may not be startable —
 * cool-down, open gaps, or a draft already in progress. Telling someone "you
 * can't" without telling them why is the fastest way to generate a support
 * ticket.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { api } from '../lib/api';
import { FrameworkMark, frameworkColorVar } from '../components/FrameworkMark';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import {
  Card, Button, Pill, EmptyState, Skeleton, ErrorNotice, ProgressBar, TONE_FOR_STATUS,
} from '../components/ui';
import { IconAssessment } from '../components/icons';

interface Submission {
  id: string; status: string; cycleYear: number;
  answeredCount: number; totalCount: number; complianceRate: number;
  finalizedAt: string | null; createdAt: string;
  framework: { id: string; key: string; name: string };
}

interface FrameworkRow {
  key: string; name: string; version: string; licensed: boolean;
}

interface Eligibility {
  allowed: boolean; reason?: string; availableAt?: string; openGaps?: number;
}

export function AssessmentsPage() {
  const { t, i18n } = useTranslation();
  const { allows } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const describeError = useApiError();
  const [error, setError] = useState<string | null>(null);

  const { data: submissions, isLoading } = useQuery({
    queryKey: ['submissions'],
    queryFn: () => api.get<Submission[]>('/submissions'),
  });

  const { data: frameworks } = useQuery({
    queryKey: ['frameworks'],
    queryFn: () => api.get<FrameworkRow[]>('/frameworks'),
  });

  const start = useMutation({
    mutationFn: (frameworkKey: string) =>
      api.post<{ id: string }>('/submissions', { frameworkKey }),
    onSuccess: (submission) => {
      void queryClient.invalidateQueries({ queryKey: ['submissions'] });
      navigate(`/assessments/${submission.id}`);
    },
    onError: (err) => setError(describeError(err)),
  });

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso));

  const licensed = frameworks?.filter((f) => f.licensed) ?? [];

  if (isLoading) {
    return (
      <div className="oc-page space-y-4">
        <Skeleton className="h-9 w-48" />
        {[0, 1].map((i) => <Skeleton key={i} className="h-32" />)}
      </div>
    );
  }

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header>
        <h1 className="oc-h1">{t('assessment.title')}</h1>
        <p className="oc-muted mt-1">{t('assessment.subtitle')}</p>
      </header>

      {error && <ErrorNotice message={error} />}

      {/* Startable frameworks */}
      {allows('submission.answer') && licensed.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {licensed.map((fw) => (
            <StartCard
              key={fw.key}
              framework={fw}
              onStart={() => { setError(null); start.mutate(fw.key); }}
              starting={start.isPending}
            />
          ))}
        </div>
      )}

      {/* History */}
      <Card>
        <h2 className="oc-h2 mb-4">{t('assessment.title')}</h2>
        {!submissions || submissions.length === 0 ? (
          <EmptyState
            icon={<IconAssessment className="w-10 h-10" />}
            title={t('assessment.noneYet')}
          />
        ) : (
          <ul className="divide-y divide-line -mx-1">
            {submissions.map((s) => (
              <li key={s.id} className="py-4 px-1 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <p className="font-medium text-ink truncate">{s.framework.name}</p>
                    <p className="text-xs text-ink-muted mt-0.5">
                      {s.cycleYear}
                      {s.finalizedAt && ` · ${t('assessment.finalized', { date: formatDate(s.finalizedAt) })}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Pill tone={TONE_FOR_STATUS[s.status] ?? 'neutral'}>
                      {t(`status.${s.status}`, {
                        defaultValue: s.status === 'REOPENED' ? 'Reopened' : s.status,
                      })}
                    </Pill>
                    <Button size="sm" onClick={() => navigate(`/assessments/${s.id}`)}>
                      {/* DRAFT and REOPENED are both editable → "Continue";
                          FINALIZED is a locked record → "Review". */}
                      {s.status === 'DRAFT' || s.status === 'REOPENED'
                        ? t('assessment.continue')
                        : t('assessment.review')}
                    </Button>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                  <p className="text-xs text-ink-muted shrink-0 w-44">
                    {t('assessment.progress', { answered: s.answeredCount, total: s.totalCount })}
                  </p>
                  <div className="flex-1">
                    <ProgressBar
                      value={s.complianceRate}
                      label={`${s.framework.name} compliance`}
                      tone={s.complianceRate >= 80 ? 'good' : s.complianceRate >= 50 ? 'serious' : 'critical'}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/**
 * A single framework's "start" affordance.
 *
 * Queries eligibility separately so the button can explain itself when disabled.
 */
function StartCard({ framework, onStart, starting }: {
  framework: FrameworkRow;
  onStart: () => void;
  starting: boolean;
}) {
  const { t, i18n } = useTranslation();

  const { data: eligibility } = useQuery({
    queryKey: ['eligibility', framework.key],
    queryFn: () => api.get<Eligibility>(`/submissions/eligibility/${framework.key}`),
  });

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso));

  const reasonText = () => {
    if (!eligibility || eligibility.allowed) return null;
    switch (eligibility.reason) {
      case 'DRAFT_ALREADY_OPEN':
        return t('assessment.draftOpen');
      case 'COOLDOWN_ACTIVE':
        return eligibility.availableAt
          ? t('assessment.cooldown', { date: formatDate(eligibility.availableAt) })
          : null;
      case 'OPEN_GAPS_REMAIN':
        return t('assessment.openGapsBlock', { count: eligibility.openGaps ?? 0 });
      default:
        return null;
    }
  };

  const blocked = eligibility ? !eligibility.allowed : false;
  const reason = reasonText();

  return (
    // A hairline in the framework's own colour along the top edge. It reads as
    // identity at a glance without adding another block of colour to a grid
    // that already has seven cards competing for attention.
    <Card className="flex flex-col relative overflow-hidden">
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-[3px]"
        style={{ background: `rgb(${frameworkColorVar(framework.key)} / 0.85)` }}
      />
      <div className="flex items-start gap-3 mb-4">
        <FrameworkMark frameworkKey={framework.key} />
        <div className="min-w-0">
          {/* Wraps rather than truncates: "EU Cyber Resilience Act" must not
              become "EU Cyber Resi…" when the whole point is recognising it. */}
          <p className="font-medium text-ink leading-snug">{framework.name}</p>
          <p className="text-xs text-ink-muted mt-0.5">{framework.version}</p>
        </div>
      </div>
      <Button
        variant="primary"
        className="w-full mt-auto"
        onClick={onStart}
        disabled={blocked || starting}
      >
        {t('assessment.start')}
      </Button>
      {reason && <p className="mt-2 text-xs text-ink-muted leading-relaxed">{reason}</p>}
    </Card>
  );
}
